import { GridRowModel } from '@mui/x-data-grid-premium';
import { cloneDeep, isEmpty, isEqual, omit } from 'lodash';
import { CalculationType, CostLine, Measurement } from 'sr-types/lib/opportunity/v1/graphql';
import { getDefaultDiscountFromSettings, isCompleteMoney } from '../../../supply/booking/Editor/BookingUtils';
import { isMoney } from '../../utils/money';
import {
    CALC_TYPE_TO_ATTRIBUTE,
    CALC_TYPE_TO_UNIT,
    DiscountCalculationTypesEnum,
    FlatCalcType,
    PercentCalcType,
    adjustmentsToRows,
    costLineToRows,
    discountLevelTypes,
    findCalculationTypeByDurationUnit
} from './costLineAdapter';
import { getCostSummary } from './DiscountGrid';
import PricingLine from './PricingLine';

export default function useCostLineAdapter() {
    const isNonZeroMoney = (amount) => {
        return isMoney(amount) && (amount.units != 0 || amount.nanos != 0);
    };

    const autoPopulateAdjustments = (adjustmentSetting) => {
        return adjustmentSetting?.percent || isNonZeroMoney(adjustmentSetting?.amount);
    };
    const getCalculationType = (adjustmentSetting) => {
        let calcType = {
            code: undefined,
            assignTo: 'baseTotal',
            formula: undefined,
            label: undefined
        };
        if (adjustmentSetting?.percent) {
            calcType = {
                code: 'pct',
                assignTo: 'baseTotal',
                formula: '(itemPrice * itemPercent)/100',
                label: 'Percentage'
            };
        } else if (isNonZeroMoney(adjustmentSetting?.amount)) {
            calcType = {
                code: 'flt',
                assignTo: 'baseTotal',
                formula: 'itemPrice',
                label: 'Flat'
            };
        }
        return calcType;
    };

    const getEmptyAdjustments = (adjustmentSetting) => {
        return [
            {
                lineType: 'Adjustment',
                adjustmentName: adjustmentSetting?.name || '',
                adjustmentDescription: adjustmentSetting?.description || '',
                adjustmentCategory: 'Discount',
                calculationInfo: {
                    autoCalculate: true,
                    calculationType: getCalculationType(adjustmentSetting),
                    itemPrice: undefined,
                    itemPercent: undefined,
                    baseTotal: undefined
                }
            }
        ];
    };

    const getEmptyCostLine = (
        defaultCurrency: string,
        calculationTypes: CalculationType[],
        adjustmentSetting: any,
        dateTimeDuration: Measurement = undefined
    ): CostLine => {
        const calculationType = dateTimeDuration
            ? findCalculationTypeByDurationUnit(calculationTypes, dateTimeDuration.unit)
            : {
                  code: undefined,
                  assignTo: 'baseTotal',
                  formula: undefined,
                  label: undefined
              };
        const costLine: CostLine = {
            lineCurrency: defaultCurrency,
            lineTotal: {},
            lineCalculationInfo: {
                lineType: 'CostLine',
                calculationInfo: {
                    autoCalculate: true,
                    calculationType: calculationType,
                    itemPrice: undefined,
                    itemQuantity: 1,
                    baseTotal: undefined,
                    attributes: undefined,
                    associatedFieldDefinitionReferences: [],
                    leaseDuration: {
                        unit: dateTimeDuration ? dateTimeDuration.unit : undefined,
                        value: dateTimeDuration ? dateTimeDuration.value : undefined
                    }
                }
            },
            adjustments: autoPopulateAdjustments(adjustmentSetting) ? getEmptyAdjustments(adjustmentSetting) : [],
            costSummary: {
                discountedTotal: {},
                discount: {},
                base: {},
                total: {},
                taxes: {}
            }
        };
        return costLine;
    };

    // Atm the assumtion is only 1 row/cost line.
    const rowsToCostLine = (
        rows: GridRowModel[],
        calculationTypes: CalculationType[],
        autoCalculate: boolean,
        adjustmentSetting: any
    ): CostLine => {
        if (rows && rows.length === 1) {
            const row: PricingLine = rows[0] as PricingLine;
            // Find calc type or select first one.
            const type: CalculationType = row.calculationType
                ? calculationTypes.find((t) => t.code === row.calculationType)
                : !isEmpty(calculationTypes)
                  ? calculationTypes[0]
                  : undefined;
            let filteredAttributes = row.attributes || [];
            let filteredFieldDefinitionRefs = row.associatedFieldDefinitionReferences || [];
            const selectedAreaAttributeName = CALC_TYPE_TO_ATTRIBUTE[row.calculationType as string];
            const line: CostLine = type
                ? {
                      lineCurrency: row.currency,
                      lineTotal: isMoney(row.discountedTotal)
                          ? row.discountedTotal
                          : isMoney(row.baseTotal)
                            ? row.baseTotal
                            : {}, // TODO This will be calculated in the future not it equals discountedTotal or baseTotal.
                      lineCalculationInfo: {
                          lineType: 'CostLine',
                          calculationInfo: {
                              autoCalculate: autoCalculate,
                              calculationType: {
                                  code: row.calculationType,
                                  assignTo: type.assignTo,
                                  formula: type.formula,
                                  label: type.label
                              },
                              itemPrice: row.itemPrice,
                              itemQuantity: row.itemQuantity,
                              baseTotal: row.baseTotal,
                              leaseDuration: {
                                  unit: CALC_TYPE_TO_UNIT[row.calculationType],
                                  value: row.leaseDuration
                              },
                              attributes: filteredAttributes.map((attribute) => {
                                  if (attribute.name !== selectedAreaAttributeName) {
                                      return attribute;
                                  }

                                  let parsedValue: Record<string, unknown> = {};
                                  try {
                                      parsedValue = attribute.value ? JSON.parse(attribute.value) : {};
                                  } catch {
                                      parsedValue = {};
                                  }

                                  const numericArea = Number(row?.area);
                                  const nextArea = Number.isFinite(numericArea) ? numericArea : 0;

                                  return {
                                      ...attribute,
                                      value: JSON.stringify({
                                          ...parsedValue,
                                          value: nextArea
                                      })
                                  };
                              }),
                              associatedFieldDefinitionReferences: filteredFieldDefinitionRefs
                          }
                      },
                      adjustments: row.adjustments,
                      costSummary: {
                          discountedTotal: isEmpty(row.discountedTotal)
                              ? row.baseTotal || {}
                              : row.discountedTotal || {},
                          discount: row.discounts || {},
                          base: row.baseTotal || {},
                          total: isEmpty(row.discountedTotal) ? row.baseTotal || {} : row.discountedTotal || {},
                          taxes: {}
                      }
                  }
                : getEmptyCostLine(row.currency, calculationTypes, adjustmentSetting);
            if (row.identity) {
                line.identity = {
                    id: row.identity,
                    preSave: row.preSave
                };
            }
            return line;
        }
    };

    const rowsToAdjustments = (rows, calculationTypes, adjustmentSetting, costLineBaseTotal, autoCalculate) => {
        if (!rows || rows.length === 0) {
            return [];
        }
        let adjustments = [];
        rows.forEach((row) => {
            const type: CalculationType = row.calculationType
                ? calculationTypes.find((t) => t.code === row.calculationType)
                : calculationTypes[0];

            const adjustment = {
                lineType: 'Adjustment',
                adjustmentName: row.adjustmentName,
                adjustmentDescription: row.adjustmentDescription,
                adjustmentCategory: 'Discount',
                calculationInfo: {
                    autoCalculate: autoCalculate,
                    calculationType: {
                        code: row.calculationType,
                        assignTo: type.assignTo,
                        formula: type.formula,
                        label: type.label
                    },
                    itemPrice:
                        row.calculationType === DiscountCalculationTypesEnum.flt
                            ? row.itemPercent || adjustmentSetting.amount
                            : row.itemPrice || costLineBaseTotal,
                    itemPercent:
                        row.itemPercent ||
                        (row.calculationType === DiscountCalculationTypesEnum.flt
                            ? adjustmentSetting.amount
                            : adjustmentSetting.percent),
                    baseTotal: row.baseTotal
                }
            };
            adjustments.push(adjustment);
        });
        return adjustments.length ? adjustments : getEmptyAdjustments(adjustmentSetting);
    };

    const toLineItemInput = (li) => {
        return omit(li, [
            'createReservation',
            'isDraftLineItem',
            'reference',
            'reservation',
            'reservationGuestsAvailable',
            'showReservationGuests',
            'security',
            'packageType',
            'availableLocations',
            'allowAdditionalLocations',
            'orderStatus',
            'productType',
            'productSubType',
            'isModified'
        ]);
    };

    const reCalculateCostlineSummary = (li, calculator, calculationTypes, discountSetting) => {
        li.costLine.costSummary = getCostSummary(li.costLine.adjustments, li.costLine);
        const newCostlineRow = costLineToRows(li.costLine, calculator, undefined);
        li.costLine = rowsToCostLine(newCostlineRow, calculationTypes, true, discountSetting);
        return li;
    };

    const applyDiscount = (calculator, opportunity, calculationTypes, applyLineLevelDiscount = false) => {
        let updateLineItems = false;
        let lineItems = [];
        if (opportunity.discountLevel === discountLevelTypes.Booking || applyLineLevelDiscount) {
            lineItems = opportunity.lineItems.map((li) => {
                const discountTobeApplied = getDefaultDiscountFromSettings(
                    opportunity.discountSetting,
                    opportunity.productBasedDiscountSetting,
                    li.productType,
                    li.productSubType
                );
                if (isCompleteMoney(discountTobeApplied.amount) || discountTobeApplied.percent) {
                    const oldSummary = li.costLine?.costSummary ? cloneDeep(li.costLine.costSummary) : {};
                    if (li.costLine && !isEmpty(li.costLine)) {
                        const emptyAdjustments = getEmptyAdjustments(discountTobeApplied);
                        if (discountTobeApplied.percent) {
                            emptyAdjustments[0].calculationInfo.calculationType = PercentCalcType;
                        } else if (isCompleteMoney(discountTobeApplied.amount)) {
                            emptyAdjustments[0].calculationInfo.calculationType = FlatCalcType;
                        }
                        li.costLine.adjustments = emptyAdjustments;
                        const newAdjustmentRow = adjustmentsToRows(
                            li.costLine.adjustments,
                            discountTobeApplied,
                            li.costLine.lineCalculationInfo.calculationInfo.baseTotal,
                            li.costLine.lineCurrency,
                            calculator
                        );
                        li.costLine.adjustments = rowsToAdjustments(
                            newAdjustmentRow,
                            calculationTypes,
                            discountTobeApplied,
                            li.costLine.lineCalculationInfo.calculationInfo.baseTotal,
                            true
                        );
                        li.costLine.adjustments = li.costLine.adjustments.map((adjustment) => {
                            if (adjustment.calculationInfo.calculationType.code === DiscountCalculationTypesEnum.flt) {
                                delete adjustment.calculationInfo.itemPercent;
                            }
                            return adjustment;
                        });
                        li = reCalculateCostlineSummary(li, calculator, calculationTypes, discountTobeApplied);
                    }
                    if (
                        li.costLine &&
                        !isEmpty(li.costLine) &&
                        !isEqual(oldSummary.discount, li.costLine.costSummary.discount)
                    ) {
                        updateLineItems = true;
                        li.isModified = true;
                    }
                } else if (li.costLine && !isEmpty(li.costLine)) {
                    updateLineItems = true;
                    li.isModified = true;
                    li.costLine.adjustments = [];
                    li = reCalculateCostlineSummary(li, calculator, calculationTypes, opportunity.discountSetting);
                }
                return li;
            });
        }
        return { lineItems, updateLineItems };
    };

    return {
        getEmptyCostLine,
        getEmptyAdjustments,
        rowsToCostLine,
        rowsToAdjustments,
        applyDiscount,
        autoPopulateAdjustments,
        toLineItemInput
    };
}
